import { EntityLevel } from '@/composables/useEntityLevel'
import { compileDashboardFeature, DashboardComponentType } from '@/features/dashboard'
import { formatPercentRounded, formatCount } from '@/features/analytics/formats'

const enabledEntityLevels: EntityLevel[] = [
  'COMPLEX',
  'REAL_ESTATE',
  'LEVEL',
  'SPACE',
  'FLOOR',
  'CONSTRUCTION'
]

export const utilization = compileDashboardFeature({
  name: 'utilization',
  title: {
    sv: 'Användning',
    en: 'Utilization'
  },
  dataSectionTitle: {
    sv: 'Användning',
    en: 'Utilization'
  },
  icon: 'IconCalendarCheck',
  enabled: false,
  defaultFilters: {
    calendar: {
      quickSelect: 'last30days'
    },
    defaultDateSelection: 'last30d',
    onlyBusinessDays: false,
    onlyBusinessHours: false,
    onlyBusinessHoursFilterType: 'query',
    dateSelections: [
      'now',
      'last24h',
      'yesterday',
      'last7d',
      'last30d',
      'lastquarter',
      'last12m',
      'currentyear',
      'lastyear',
      'custom'
    ]
  },
  measurements: [
    {
      name: 'presence_10_minutes',
      field: {
        name: 'utilization',
        expression: {
          type: 'avg',
          exp: {
            type: 'field',
            name: 'used'
          }
        }
      },
      title: {
        sv: 'Användning',
        en: 'Utilization'
      },
      options: {
        color: '#40A9FF',
        enabledEntityLevels,
        format: formatPercentRounded,
        min: 0,
        max: 1
      }
    },
    {
      name: 'presence_10_minutes',
      field: {
        name: 'activity',
        expression: {
          type: 'sum',
          exp: {
            type: 'field',
            name: 'count_sum'
          }
        }
      },
      title: {
        sv: 'Antal rörelsedetektioner',
        en: 'Number of Motion Detections'
      },
      options: {
        color: '#FF7A45',
        enabledEntityLevels,
        format: formatCount,
        legend: {
          type: 'slider'
        }
      }
    }
  ],
  components: [
    {
      type: DashboardComponentType.Kpi,
      name: 'utilization-presence-10-minutes-kpi',
      measurements: ['presence_10_minutes.utilization'],
      info: {
        sv: 'Användning över tid adderas per 10 minuter, summan divideras sedan med det totala antalet datapunkter',
        en: 'Usage over time is added per 10 minutes, the sum is then divided by the total number of data points.'
      }
    },
    {
      type: DashboardComponentType.Kpi,
      name: 'activity-presence-10-minutes-kpi',
      measurements: ['presence_10_minutes.activity'],
      info: {
        sv: 'Användning över tid adderas, summan divideras sedan med det totala antalet datapunkter',
        en: 'Usage over time is added, the sum is then divided by the total number of data points.'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'timeseries-presence',
      measurements: ['presence_10_minutes.utilization'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }]
          }
        }
      },
      title: {
        sv: 'Användning över tid',
        en: 'Utilization over time'
      },
      info: {
        sv: 'Tidsserie över mätvärden',
        en: 'Time series of measurements'
      }
    },
    {
      type: DashboardComponentType.TimeSeries,
      name: 'timeseries-activity-presence',
      measurements: ['presence_10_minutes.activity'],
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'path_subtree' }]
          }
        }
      },
      title: {
        sv: 'Aktivitet över tid',
        en: 'Activity over time'
      },
      info: {
        sv: 'Tidsserie över mätvärden',
        en: 'Time series of measurements'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'bar-chart-presence-hourly',
      measurements: ['presence_10_minutes.utilization'],
      groupKey: 'timestamp_extract_hour',
      codomainTicks: {
        values: [0, 0.5, 1]
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'HOUR' }]
          }
        }
      },
      title: {
        sv: 'Användning över timmar',
        en: 'Usage over hours'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning aggregerat över timmar.',
        en: 'The bar chart shows selected usage aggregated over hours.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'bar-chart-activity-presence-hourly',
      measurements: ['presence_10_minutes.activity'],
      groupKey: 'timestamp_extract_hour',
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'HOUR' }]
          }
        }
      },
      title: {
        sv: 'Aktivitet över timmar',
        en: 'Activity over hours'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av aktivitet aggregerat över timmar.',
        en: 'The bar chart shows selected activity aggregated over hours.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'bar-chart-presence-weekday',
      measurements: ['presence_10_minutes.utilization'],
      groupKey: 'timestamp_extract_isodow',
      codomainTicks: {
        values: [0, 0.5, 1]
      },
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'ISODOW' }]
          }
        }
      },
      title: {
        sv: 'Användning över veckodagar',
        en: 'Usage over weekdays'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av användning aggregerat över veckodag.',
        en: 'The bar chart shows the selected type of usage aggregated over weekdays.'
      }
    },
    {
      type: DashboardComponentType.BarChart,
      name: 'bar-chart-activity-presence-weekday',
      measurements: ['presence_10_minutes.activity'],
      groupKey: 'timestamp_extract_isodow',
      measurementOverride: {
        options: {
          colorScale: null
        }
      },
      filters: {
        values: {
          query: {
            groupBy: [{ type: 'timestamp_extract', extract: 'ISODOW' }]
          }
        }
      },
      title: {
        sv: 'Aktivitet över veckodagar',
        en: 'Activity over weekdays'
      },
      info: {
        sv: 'Stapeldiagrammet visar vald typ av aktivitet aggregerat över veckodag.',
        en: 'The bar chart shows the selected type of activity aggregated over weekdays.'
      }
    }
  ],
  tabs: [
    {
      name: 'presence_activity',
      title: { sv: 'Aktivitet', en: 'Activity' },
      components: [
        'activity-presence-10-minutes-kpi',
        'timeseries-activity-presence',
        'bar-chart-activity-presence-hourly',
        'bar-chart-activity-presence-weekday'
      ],
      initialShowOnMapComponent: 'activity-presence-10-minutes-kpi'
    },
    {
      name: 'presence_utilization',
      title: { sv: 'Användning', en: 'Utilization' },
      components: [
        'utilization-presence-10-minutes-kpi',
        'timeseries-presence',
        'bar-chart-presence-hourly',
        'bar-chart-presence-weekday'
      ],
      initialShowOnMapComponent: 'utilization-presence-10-minutes-kpi'
    }
  ]
})
